import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useChat } from '../../chat/ChatProvider';
import { useTranslation } from '../../i18n/LocaleContext';
import EmotePickerSheet from './EmotePickerSheet';
import MentionPicker from './MentionPicker';

/** 偵測游標前未完成的 @query（空白後起算）。 */
function detectMentionQuery(text, caret) {
  const before = String(text || '').slice(0, caret ?? 0);
  const match = before.match(/(^|[\s([{（【])@([A-Za-z0-9_]*)$/);
  if (!match) return null;
  return {
    query: match[2] || '',
    start: before.length - (match[2] || '').length - 1,
  };
}

function isPermanentMute(imMuteUntil) {
  if (!imMuteUntil) return false;
  const year = new Date(imMuteUntil).getFullYear();
  return Number.isFinite(year) && year >= 9000;
}

function isImMuted(imMuteUntil) {
  if (!imMuteUntil) return false;
  if (isPermanentMute(imMuteUntil)) return true;
  const until = new Date(imMuteUntil).getTime();
  return Number.isFinite(until) && until > Date.now();
}

function fill(template, params = {}) {
  let text = template || '';
  Object.entries(params).forEach(([k, v]) => {
    text = text.replaceAll(`{${k}}`, String(v));
  });
  return text;
}

function formatMuteRemaining(imMuteUntil, t) {
  if (isPermanentMute(imMuteUntil)) {
    return t('chat.mutePermanent');
  }
  const until = new Date(imMuteUntil).getTime();
  if (!Number.isFinite(until)) {
    return '';
  }
  const ms = Math.max(0, until - Date.now());
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    let text = fill(t('chat.muteRemainingDays'), { 0: days });
    if (hours > 0) {
      text = `${text} ${fill(t('chat.muteRemainingHours'), { 0: hours })}`;
    }
    return text;
  }
  if (hours > 0) {
    let text = fill(t('chat.muteRemainingHours'), { 0: hours });
    if (minutes > 0) {
      text = `${text} ${fill(t('chat.muteRemainingMinutes'), { 0: minutes })}`;
    }
    return text;
  }
  return fill(t('chat.muteRemainingMinutes'), { 0: Math.max(1, minutes) });
}

export default function Composer({ conversationId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    state,
    dispatch,
    sendText,
    editMessage,
    sendImage,
    sendAudio,
    sendVideo,
    sendFile,
    sendLocation,
    openPicker,
    notifyTyping,
  } = useChat();
  const draft = state.composerDrafts[String(conversationId)] || '';
  const replyTo = state.replyTargets[String(conversationId)];
  const editTarget = state.editTargets[String(conversationId)];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const inputRef = useRef(null);
  const muted = useMemo(() => isImMuted(user?.imMuteUntil), [user?.imMuteUntil]);
  const muteTip = useMemo(() => {
    if (!muted) return '';
    if (isPermanentMute(user?.imMuteUntil)) {
      return t('chat.mutedPermanent');
    }
    const remaining = formatMuteRemaining(user?.imMuteUntil, t);
    return fill(t('chat.mutedUntil'), { remaining });
  }, [muted, user?.imMuteUntil, t]);

  useEffect(() => {
    setMentionIds(
      Array.isArray(editTarget?.mentionMemberIds)
        ? editTarget.mentionMemberIds.map(String)
        : []
    );
    setMentionQuery(null);
  }, [editTarget?.serverMessageId, conversationId]);

  // textarea 依內容自動增高，超過上限後改為內部捲動，避免裁字並保持送出鈕可見。
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    const maxHeight = 120;
    node.style.height = 'auto';
    const nextHeight = Math.min(node.scrollHeight, maxHeight);
    node.style.height = `${Math.max(nextHeight, 40)}px`;
    node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [draft, editTarget?.serverMessageId, conversationId]);

  const onSend = async () => {
    const text = draft.trim();
    if (!text || busy || muted) return;
    setBusy(true);
    setError('');
    const previousDraft = text;
    const previousMentions = [...mentionIds];
    const ids = mentionIds.map(Number).filter((n) => Number.isFinite(n));
    dispatch({ type: 'DRAFT_SET', conversationId, draft: '' });
    setMentionIds([]);
    setMentionQuery(null);
    try {
      if (editTarget?.serverMessageId) {
        await editMessage(conversationId, editTarget.serverMessageId, text, ids);
      } else {
        await sendText(conversationId, text, ids);
      }
    } catch (err) {
      setError(err?.message || (editTarget ? '編輯失敗' : '發送失敗'));
      dispatch({ type: 'DRAFT_SET', conversationId, draft: previousDraft });
      setMentionIds(previousMentions);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && mentionQuery) {
      e.preventDefault();
      setMentionQuery(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const onDraftChange = (e) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;
    dispatch({ type: 'DRAFT_SET', conversationId, draft: value });
    setMentionQuery(detectMentionQuery(value, caret));
    if (value.trim()) {
      notifyTyping?.(conversationId, true);
    } else {
      notifyTyping?.(conversationId, false);
    }
  };

  const syncMentionQueryFromInput = () => {
    const el = inputRef.current;
    if (!el) return;
    setMentionQuery(detectMentionQuery(el.value, el.selectionStart ?? el.value.length));
  };

  const insertMention = (row) => {
    const token = row.username || row.displayName || row.label;
    if (!token || mentionQuery == null) return;
    const start = mentionQuery.start;
    const el = inputRef.current;
    const caret = el?.selectionStart ?? draft.length;
    const before = draft.slice(0, start);
    const after = draft.slice(caret);
    const next = `${before}@${token} ${after}`;
    dispatch({ type: 'DRAFT_SET', conversationId, draft: next });
    setMentionIds((prev) => {
      const key = String(row.memberId);
      return prev.includes(key) ? prev : [...prev, key];
    });
    setMentionQuery(null);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const pos = before.length + token.length + 2;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
    });
  };

  const pickFile = async (file, kind) => {
    if (!file || muted) return;
    setBusy(true);
    setError('');
    try {
      if (kind === 'image') await sendImage(conversationId, file);
      else if (kind === 'audio') await sendAudio(conversationId, file);
      else if (kind === 'video') await sendVideo(conversationId, file);
      else await sendFile(conversationId, file);
    } catch (err) {
      setError(err?.message || '上傳失敗');
    } finally {
      setBusy(false);
    }
  };

  const shareLocation = () => {
    if (muted) return;
    if (!navigator.geolocation) {
      setError('裝置不支援定位');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await sendLocation(conversationId, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            locationName: '目前位置',
          });
        } catch (err) {
          setError(err?.message || '位置發送失敗');
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        setError('無法取得位置');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="chat-composer">
      {muted ? (
        <div className="chat-composer__muted" role="status">
          {muteTip}
        </div>
      ) : null}
      {!muted && editTarget ? (
        <div className="chat-composer__reply">
          <span>編輯訊息</span>
          <button
            type="button"
            className="chat-link-btn"
            onClick={() => {
              dispatch({ type: 'EDIT_SET', conversationId, editTarget: null });
              dispatch({ type: 'DRAFT_SET', conversationId, draft: '' });
            }}
          >
            取消
          </button>
        </div>
      ) : null}
      {!muted && !editTarget && replyTo ? (
        <div className="chat-composer__reply">
          <span>回覆：{replyTo.previewText || replyTo.serverMessageId}</span>
          <button
            type="button"
            className="chat-link-btn"
            onClick={() => dispatch({ type: 'REPLY_SET', conversationId, replyTo: null })}
          >
            取消
          </button>
        </div>
      ) : null}
      {error ? <div className="chat-composer__error">{error}</div> : null}
      {!muted ? (
        <>
      <div className="chat-composer__tools">
        <button type="button" onClick={() => openPicker('stickers')} disabled={busy || !!editTarget}>
          貼圖
        </button>
        <button type="button" onClick={() => openPicker('gifs')} disabled={busy || !!editTarget}>
          GIF
        </button>
        <button
          type="button"
          onClick={() => imageRef.current?.click()}
          disabled={busy || !!editTarget}
        >
          圖片
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy || !!editTarget}
        >
          檔案
        </button>
        <button type="button" onClick={shareLocation} disabled={busy || !!editTarget}>
          位置
        </button>
      </div>
      <div className="chat-composer__row">
        {mentionQuery ? (
          <MentionPicker
            conversationId={conversationId}
            query={mentionQuery.query}
            onPick={insertMention}
            onClose={() => setMentionQuery(null)}
          />
        ) : null}
        <textarea
          ref={inputRef}
          className="chat-composer__input"
          rows={1}
          placeholder={editTarget ? '編輯訊息…' : '輸入訊息…（@ 提及成員）'}
          value={draft}
          disabled={busy}
          onChange={onDraftChange}
          onKeyDown={onKeyDown}
          onClick={syncMentionQueryFromInput}
          onKeyUp={syncMentionQueryFromInput}
        />
        <button
          type="button"
          className="chat-composer__send"
          disabled={busy || !draft.trim()}
          onClick={onSend}
        >
          {editTarget ? '儲存' : '送出'}
        </button>
      </div>
      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          const kind = f?.type?.startsWith('video/')
            ? 'video'
            : f?.type?.startsWith('audio/')
              ? 'audio'
              : 'image';
          pickFile(f, kind);
        }}
      />
      <input
        ref={fileRef}
        type="file"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (f.type.startsWith('video/')) pickFile(f, 'video');
          else if (f.type.startsWith('audio/')) pickFile(f, 'audio');
          else if (f.type.startsWith('image/')) pickFile(f, 'image');
          else pickFile(f, 'file');
        }}
      />
      <EmotePickerSheet conversationId={conversationId} />
        </>
      ) : null}
    </div>
  );
}
